"use client";

/**
 * Site config provider + hook.
 *
 * `LiveSiteConfigProvider` mounts once at the root of the public site. It owns
 * the live polling of /site-config.json and exposes the current value via
 * React context; components read it with `useSiteConfig()`.
 *
 * First render (server prerender AND first client paint) always uses
 * `defaultConfig` so there's no hydration mismatch. After mount, the effect
 * swaps in any localStorage-cached config, then fetches the live one and polls
 * every 60s (matching the CloudFront TTL on /site-config.json).
 *
 * `SiteConfigProvider` (takes a `config` prop, no polling) is used by the admin
 * preview to inject a draft config for a subtree.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  defaultConfig,
  normalizeSiteConfig,
  type SiteConfig,
} from "./siteConfig";

const SiteConfigContext = createContext<SiteConfig | null>(null);

const CONFIG_URL = "/site-config.json";
const STORAGE_KEY = "varano.site-config";
const POLL_MS = 60_000;

function readCachedConfig(): SiteConfig | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return normalizeSiteConfig(JSON.parse(raw) as Partial<SiteConfig>);
  } catch {
    return null;
  }
}

function cacheConfig(config: SiteConfig) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // ignore storage write failures (private mode, quota, etc.)
  }
}

async function fetchConfig(): Promise<SiteConfig | null> {
  try {
    const res = await fetch(CONFIG_URL, { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = normalizeSiteConfig((await res.json()) as Partial<SiteConfig>);
    cacheConfig(data);
    return data;
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[SiteConfig] fetch failed:", err);
    }
    return null;
  }
}

export function LiveSiteConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<SiteConfig>(defaultConfig);

  useEffect(() => {
    let cancelled = false;
    const apply = (next: SiteConfig | null) => {
      if (next && !cancelled) setConfig(next);
    };

    // Show the cached config immediately (fast), then reconcile with the live one.
    apply(readCachedConfig());
    fetchConfig().then(apply);

    const interval = setInterval(() => {
      fetchConfig().then(apply);
    }, POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return (
    <SiteConfigContext.Provider value={config}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function SiteConfigProvider({
  config,
  children,
}: {
  config: SiteConfig;
  children: ReactNode;
}) {
  return (
    <SiteConfigContext.Provider value={config}>
      {children}
    </SiteConfigContext.Provider>
  );
}

/** Read the current site config. Falls back to defaults outside a provider. */
export function useSiteConfig(): SiteConfig {
  return useContext(SiteConfigContext) ?? defaultConfig;
}
